/**
 * Workflow data for the semantic registry.
 * Separated from query logic to respect the 250-line file limit.
 */
import type { WorkflowEntry } from './semantic-registry';

export const WORKFLOWS: WorkflowEntry[] = [
  {
    workflow: 'crm-dashboard',
    label: 'CRM Dashboard',
    description: 'Sales pipeline with KPIs, funnel, and data table.',
    components: [
      { id: 'mn-dashboard', role: 'schema-driven grid' },
      { id: 'mn-gauge', role: 'KPI display' },
      { id: 'mn-chart', role: 'trend visualization' },
      { id: 'mn-funnel', role: 'pipeline stages' },
      { id: 'mn-data-table', role: 'record list' },
    ],
    compositionRules: ['kpi-dashboard', 'filterable-table'],
    layout: '4-slot grid (createLayout)',
    tags: ['dashboard', 'crm', 'sales', 'pipeline', 'kpi', 'metrics'],
    starter: { package: '@convergio/shared-shell-starter', description: 'App shell with header, nav, and content area', entryFunction: 'createSharedShellFiles' },
    dataShapes: ['list', 'numeric-kpis', 'time-series'],
  },
  {
    workflow: 'data-browser',
    label: 'Data Browser',
    description: 'Filterable, sortable data table with detail panel.',
    components: [
      { id: 'mn-facet-workbench', role: 'filter sidebar' },
      { id: 'mn-data-table', role: 'primary data grid' },
      { id: 'mn-detail-panel', role: 'record detail slide-out' },
    ],
    compositionRules: ['filterable-table', 'crud-entity'],
    layout: 'side-detail',
    tags: ['data', 'table', 'filter', 'browse', 'crud', 'list', 'records'],
    starter: { package: '@convergio/shared-shell-starter', description: 'App shell with header, nav, and content area', entryFunction: 'createSharedShellFiles' },
    dataShapes: ['list', 'single-entity'],
  },
  {
    workflow: 'ai-chat-interface',
    label: 'AI Chat Interface',
    description: 'LLM-powered chat with streaming, voice, and actions.',
    components: [
      { id: 'mn-chat', role: 'chat UI with streaming' },
      { id: 'streamingText', role: 'LLM output renderer' },
      { id: 'voiceManager', role: 'voice input adapter' },
    ],
    compositionRules: ['ai-chat'],
    layout: 'full page or side panel',
    tags: ['ai', 'chat', 'llm', 'streaming', 'voice', 'conversational'],
    dataShapes: ['streaming-chat'],
  },
  {
    workflow: 'project-planner',
    label: 'Project Planner',
    description: 'Timeline with task hierarchy, OKRs, and kanban board.',
    components: [
      { id: 'mn-gantt', role: 'timeline visualization' },
      { id: 'mn-kanban-board', role: 'task workflow board' },
      { id: 'mn-okr', role: 'objectives and key results' },
    ],
    compositionRules: ['timeline-planning', 'kanban-workflow'],
    layout: '4-slot grid (createLayout)',
    tags: ['project', 'planning', 'gantt', 'kanban', 'tasks', 'timeline', 'okr'],
    starter: { package: '@convergio/shared-shell-starter', description: 'App shell with header, nav, and content area', entryFunction: 'createSharedShellFiles' },
    dataShapes: ['date-range-items', 'task-statuses'],
  },
  {
    workflow: 'ops-monitor',
    label: 'Operations Monitor',
    description: 'Real-time system health, gauges, and alert dashboard.',
    components: [
      { id: 'mn-system-status', role: 'service health panel' },
      { id: 'mn-gauge', role: 'utilization meters' },
      { id: 'mn-chart', role: 'live metrics' },
      { id: 'mn-toast', role: 'alert notifications' },
    ],
    compositionRules: ['monitoring-strip', 'system-health'],
    layout: 'strip + 4-slot grid',
    tags: ['monitoring', 'ops', 'health', 'status', 'alerts', 'real-time'],
    dataShapes: ['health-check', 'numeric-kpis', 'time-series'],
  },
  {
    workflow: 'entity-editor',
    label: 'Entity Editor',
    description: 'Tabbed form editor with validation and async lookups.',
    components: [
      { id: 'mn-entity-workbench', role: 'tabbed form editor' },
      { id: 'mn-async-select', role: 'entity lookup fields' },
      { id: 'mn-date-picker', role: 'date input fields' },
    ],
    compositionRules: ['crud-entity'],
    layout: 'full page or modal',
    tags: ['entity', 'form', 'editor', 'crud', 'validation', 'edit'],
    dataShapes: ['single-entity'],
  },
  {
    workflow: 'ai-ops-dashboard',
    label: 'AI Ops Dashboard',
    description: 'LLM cost tracking, token budgets, and agent traces.',
    components: [
      { id: 'tokenMeter', role: 'token budget gauge' },
      { id: 'agentCostBreakdown', role: 'per-agent cost table' },
      { id: 'costTimeline', role: 'cost trend chart' },
      { id: 'agentTrace', role: 'execution trace viewer' },
    ],
    compositionRules: ['cost-tracking'],
    layout: '4-slot grid (createLayout)',
    tags: ['ai', 'ops', 'cost', 'tokens', 'llm', 'budget', 'finops', 'dashboard'],
    dataShapes: ['cost-breakdown', 'numeric-kpis'],
  },
  {
    workflow: 'app-shell',
    label: 'Application Shell',
    description: 'Full app frame: header, navigation, theme, and layout.',
    components: [
      { id: 'mn-header-shell', role: 'app header with nav + search' },
      { id: 'mn-theme-toggle', role: 'theme switcher' },
      { id: 'mn-a11y', role: 'accessibility settings FAB' },
      { id: 'createLayout', role: '4-slot grid state machine' },
    ],
    compositionRules: ['app-shell'],
    layout: '4-slot grid (createLayout)',
    tags: ['shell', 'app', 'header', 'navigation', 'layout', 'scaffold'],
    starter: { package: '@convergio/shared-shell-starter', description: 'Config-driven app shell for Next.js / Tauri', entryFunction: 'createSharedShellFiles' },
  },
  {
    workflow: 'geo-analytics',
    label: 'Geographic Analytics',
    description: 'Map-based data with markers, clustering, and detail.',
    components: [
      { id: 'mn-map', role: 'marker map' },
      { id: 'mn-mapbox', role: 'advanced styled map' },
      { id: 'mn-detail-panel', role: 'location detail panel' },
    ],
    compositionRules: ['geographic-data'],
    layout: 'split (map + detail)',
    tags: ['map', 'geo', 'location', 'markers', 'geographic', 'spatial'],
    dataShapes: ['list'],
  },
  {
    workflow: 'customer-journey',
    label: 'Customer Journey Map',
    description: 'Phase-based journey with engagements and funnels.',
    components: [
      { id: 'mn-customer-journey', role: 'journey phase map' },
      { id: 'mn-funnel', role: 'conversion funnel' },
      { id: 'mn-chart', role: 'engagement metrics' },
    ],
    compositionRules: [],
    layout: 'full width',
    tags: ['journey', 'customer', 'funnel', 'pipeline', 'engagement'],
    dataShapes: ['list'],
  },
];
